import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, ClipboardList, Headphones, Share2, UtensilsCrossed, BookOpen } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributorDashboard = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/distributor-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);


  const today = new Date();
  const greeting = today.getHours() < 12 ? 'Buenos días' : today.getHours() < 18 ? 'Buenas tardes' : 'Buenas noches';
  const dateStr = today.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
      </div>
    );
  }

  const statsCards = [
    { icon: Users, label: 'Mis Clientes', value: stats?.my_clients || 0, color: '#3B82F6', bg: 'from-blue-50 to-indigo-50', border: 'border-blue-100/50' },
    { icon: ClipboardList, label: 'Formularios Completados', value: stats?.my_tests || 0, color: '#8B5CF6', bg: 'from-violet-50 to-purple-50', border: 'border-violet-100/50' },
    { icon: Headphones, label: 'Mis Tickets', value: stats?.my_tickets || 0, color: '#F59E0B', bg: 'from-amber-50 to-orange-50', border: 'border-amber-100/50' },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="distributor-dashboard">
      {/* Greeting */}
      <div className="pt-1">
        <p className="text-sm text-gray-400 uppercase tracking-widest font-medium capitalize">{dateStr}</p>
        <h1 className="text-4xl font-bold text-gray-900 mt-1.5" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {greeting}, <span style={{ color: '#467322' }}>{user?.name?.split(' ')[0]}</span>
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {statsCards.map((card, idx) => {
          const IconComponent = card.icon;
          return (
            <div key={card.label} className={`bg-gradient-to-br ${card.bg} rounded-2xl p-4 border ${card.border}`}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2.5" style={{ backgroundColor: `${card.color}12` }}>
                <IconComponent size={18} style={{ color: card.color }} />
              </div>
              <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{card.value}</p>
              <p className="text-xs text-gray-500 font-medium">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Profile Card */}
      <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl p-5 text-white shadow-lg shadow-violet-500/20" data-testid="distributor-profile-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold">{user?.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-xl truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{user?.name}</p>
            {user?.display_id && (
              <span className="inline-block text-xs bg-white/20 backdrop-blur rounded-full px-3 py-0.5 mt-1 font-mono">
                {user.display_id}
              </span>
            )}
            <p className="text-xs text-white/70 mt-1">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Tool Sections */}
      <div className="space-y-3">
        {[
          {
            icon: Share2,
            label: 'Formularios de Salud',
            description: 'Comparte formularios con tus clientes',
            path: '/distributor/forms',
            color: '#EF4444',
            gradient: 'from-red-500 to-rose-500',
          },
          {
            icon: UtensilsCrossed,
            label: 'Configurador de Dietas',
            description: 'Crea planes nutricionales personalizados',
            path: '/distributor/profile/diet-configurator',
            color: '#467322',
            gradient: 'from-emerald-500 to-green-500',
          },
          {
            icon: BookOpen,
            label: 'Dietas Guardadas',
            description: 'Gestiona y asigna dietas a tus clientes',
            path: '/distributor/profile/diets',
            color: '#2563EB',
            gradient: 'from-blue-500 to-indigo-500',
          },
        ].map((section, idx) => {
          const IconComp = section.icon;
          return (
            <button
              key={section.path}
              onClick={() => navigate(section.path)}
              className="w-full bg-white rounded-2xl border border-gray-100/50 p-4 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
              style={{ animationDelay: `${idx * 60}ms` }}
              data-testid={`profile-section-${section.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`rounded-2xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform duration-300`}
                  style={{ width: '52px', height: '52px', boxShadow: `0 4px 14px ${section.color}30` }}
                >
                  <IconComp size={24} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {section.label}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
                </div>
                <div className="text-gray-300 group-hover:text-gray-500 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DistributorDashboard;
